/**
 * <s-promo-popup> — opens its promo dialog once per browser session while the
 * offer is live, and copies the discount code on request.
 *
 * Attributes:
 *   expires — ISO 8601 date-time after which the popup never opens.
 *   storage-key — sessionStorage key recording that the buyer dismissed it.
 *   delay — milliseconds to wait after load before opening. Default 700.
 * Commands (on this element's id):
 *   --copy-code — copies [data-code] text and announces [data-copied-message].
 */
class SPromoPopup extends HTMLElement {
  #ac;
  #openTimer = null;
  #announceRaf = 0;

  connectedCallback() {
    const dialog = this.querySelector('dialog');
    if (!dialog) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const key = this.getAttribute('storage-key') || 'promo-popup-dismissed';
    const expires = Date.parse(this.getAttribute('expires') || '');

    this.addEventListener('command', (event) => {
      if (event.command === '--copy-code') {
        event.preventDefault();
        this.#copy();
      }
    }, opts);

    dialog.addEventListener('close', () => {
      try { sessionStorage.setItem(key, '1'); } catch (_) { /* storage unavailable */ }
    }, opts);

    if (!Number.isNaN(expires) && Date.now() > expires) return;

    let dismissed = false;
    try { dismissed = sessionStorage.getItem(key) === '1'; } catch (_) { /* storage unavailable */ }
    if (dismissed) return;

    const delay = Number(this.getAttribute('delay') || 700);
    this.#openTimer = setTimeout(() => {
      if (!dialog.open) dialog.showModal();
    }, delay);
  }

  disconnectedCallback() {
    clearTimeout(this.#openTimer);
    cancelAnimationFrame(this.#announceRaf);
    this.#ac?.abort();
  }

  async #copy() {
    const code = this.querySelector('[data-code]')?.textContent.trim();
    const status = this.querySelector('[data-copy-status]');
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
    } catch (_) {
      return;
    }
    this.toggleAttribute('data-copied', true);
    if (status) {
      cancelAnimationFrame(this.#announceRaf);
      status.textContent = '';
      this.#announceRaf = requestAnimationFrame(() => {
        status.textContent = status.dataset.copiedMessage || '';
      });
    }
  }
}

customElements.define('s-promo-popup', SPromoPopup);
